import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Calendar, FileText, Receipt, DollarSign, BarChart3, Briefcase,
  MoreVertical, ChevronRight, MapPin, ArrowUpRight, Landmark,
  Wrench, Clock, Plus,
} from "lucide-react";
import api from "../../lib/apiClient";
import { fmtCAD } from "../../lib/format";

const STATUS_TAG = {
  Sent: "Sent to customer",
  Viewed: "Viewed",
  Draft: "Draft",
  Approved: "Approved",
};

function fmtTime(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleTimeString("en-CA", { hour: "numeric", minute: "2-digit" }).toLowerCase().replace(" ", "");
  } catch { return ""; }
}
function fmtDay(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("en-CA", { weekday: "short", month: "short", day: "numeric", year: "numeric" });
  } catch { return iso; }
}

export default function HcpDashboard() {
  const [d, setD] = useState(null);
  const [money, setMoney] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const load = () => Promise.all([
      api.get("/dashboard/hcp").then(({ data }) => !cancelled && setD(data)),
      api.get("/my-money/summary").then(({ data }) => !cancelled && setMoney(data)).catch(() => !cancelled && setMoney(null)),
    ]).finally(() => !cancelled && setLoading(false));
    load();
    // Refetch when user returns to the tab or this page
    const onVis = () => { if (document.visibilityState === "visible") load(); };
    const onFocus = () => load();
    document.addEventListener("visibilitychange", onVis);
    window.addEventListener("focus", onFocus);
    return () => {
      cancelled = true;
      document.removeEventListener("visibilitychange", onVis);
      window.removeEventListener("focus", onFocus);
    };
  }, []);

  if (loading) return <div className="text-sm text-slate-500">Loading dashboard…</div>;
  if (!d) return null;

  return (
    <div data-testid="hcp-dashboard" className="space-y-6">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <UpcomingJobsCard data={d.upcoming_jobs} />
        <OpenEstimatesCard data={d.open_estimates} />
        <OpenInvoicesCard data={d.open_invoices} />
        <PaidInvoicesCard data={d.paid_invoices} />
        <TodayStatsCard data={d.today} today_iso={d.today_iso} />
        <JobsCard data={d.jobs} />
        <TimesheetCard data={d.timesheet} />
        <EstimatesDonutCard data={d.estimates_donut} />
        {/* Tax group — Corp Tax + HST side by side per user request */}
        <CorpTaxCard data={d.corp_tax} />
        <HstCard data={money?.hst} />
      </div>
    </div>
  );
}

/* ---------- Cards ---------- */

function Card({ title, icon: Icon, action, children, testid, headerRight }) {
  return (
    <section data-testid={testid} className="bg-white border border-slate-200 rounded-lg flex flex-col">
      <header className="px-5 py-3.5 border-b border-slate-100 flex items-center justify-between gap-3">
        <h3 className="font-display font-semibold text-slate-900 text-[15px] flex items-center gap-2">
          {Icon && <Icon size={16} className="text-slate-500" />}
          {title}
        </h3>
        <div className="flex items-center gap-2">
          {headerRight}
          {action && (
            <Link to={action.to} className="text-xs text-[#0F4C81] hover:underline font-medium flex items-center gap-0.5">
              {action.label} <ArrowUpRight size={12} />
            </Link>
          )}
        </div>
      </header>
      <div className="px-5 py-4 flex-1">{children}</div>
    </section>
  );
}

function UpcomingJobsCard({ data }) {
  const today = new Date();
  return (
    <Card
      title="Upcoming jobs"
      icon={Calendar}
      action={{ to: "/schedule", label: "View calendar" }}
      testid="card-upcoming-jobs"
      headerRight={
        <Link
          to="/jobs/new"
          data-testid="upcoming-jobs-new-btn"
          title="New job"
          className="p-1.5 rounded hover:bg-slate-100 text-slate-600 hover:text-[#0F4C81] transition-colors"
        >
          <span className="relative inline-block">
            <Wrench size={16} />
            <Plus size={9} className="absolute -top-1 -right-1 bg-[#0F4C81] text-white rounded-full p-[1px]" strokeWidth={3} />
          </span>
        </Link>
      }
    >
      <div className="text-xs text-slate-500 mb-3 font-medium">
        {today.toLocaleDateString("en-CA", { weekday: "short", month: "short", day: "numeric", year: "numeric" })}
      </div>
      {data.length === 0 ? (
        <div className="text-sm text-slate-400 italic py-2">No upcoming jobs</div>
      ) : (
        <ul className="space-y-3">
          {data.slice(0, 4).map((j) => (
            <li key={j.id} className="flex gap-3 text-sm">
              <div className="font-mono text-xs font-semibold text-slate-900 w-14 shrink-0 pt-0.5 tabular">
                {fmtTime(j.scheduled_start)}
              </div>
              <Link to={`/jobs/${j.id}`} className="flex-1 min-w-0 hover:bg-slate-50 -mx-2 px-2 py-1 rounded">
                <div className="font-medium text-slate-900 truncate">{j.customer_name}</div>
                <div className="text-xs text-slate-500 truncate flex items-center gap-1">
                  <MapPin size={10} /> {j.service_address || j.job_type}
                </div>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

function OpenEstimatesCard({ data }) {
  return (
    <Card title="Open estimates" icon={FileText} action={{ to: "/estimates", label: "See all" }} testid="card-open-estimates">
      {data.length === 0 ? (
        <div className="text-sm text-slate-400 italic py-2">No open estimates</div>
      ) : (
        <ul className="space-y-3">
          {data.slice(0, 4).map((e) => (
            <li key={e.id}>
              <Link to={`/estimates/${e.id}`} className="block hover:bg-slate-50 -mx-2 px-2 py-1.5 rounded">
                <div className="flex items-center justify-between gap-2">
                  <span className="font-medium text-slate-900 text-sm truncate">{e.customer_name}</span>
                  <span className="text-xs text-[#0F4C81] font-medium shrink-0">{STATUS_TAG[e.status] || e.status}</span>
                </div>
                <div className="flex items-center justify-between text-xs text-slate-500 mt-0.5">
                  <span className="truncate">{e.number} · {fmtDay(e.created_at).split(",")[0]}</span>
                  <span className="tabular font-medium text-slate-700 shrink-0">{fmtCAD(e.total)}</span>
                </div>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

function OpenInvoicesCard({ data }) {
  const navigate = useNavigate();
  const { total, past_due, due_soon, unsent } = data;
  // HCP-spec colors: Past due=RED, Due=YELLOW, Unsent=BLUE
  const segs = [
    { key: "past",   value: past_due, color: "#E11D48", label: "Past due", statusFilter: "Overdue" },
    { key: "due",    value: due_soon, color: "#F59E0B", label: "Due",      statusFilter: "Sent" },
    { key: "unsent", value: unsent,   color: "#2563EB", label: "Unsent",   statusFilter: "Draft" },
  ];
  const maxVal = Math.max(past_due, due_soon, unsent, 1);
  const go = (statusFilter) => navigate(`/invoices?status=${encodeURIComponent(statusFilter)}`);

  return (
    <Card
      title="Open invoices"
      icon={Receipt}
      action={{ to: "/invoices?status=all", label: "View all" }}
      testid="card-open-invoices"
    >
      {/* Big open-invoices dollar total top-right */}
      <div className="flex items-start justify-end mb-3">
        <div className="text-right">
          <div data-testid="open-invoices-total" className="font-display text-3xl font-bold tabular text-slate-900">{fmtCAD(total)}</div>
          <div className="text-[11px] text-slate-500 mt-0.5">Open invoices</div>
        </div>
      </div>

      {/* Three bars with COLORED BASELINES — always render, never blank even at $0 */}
      <div className="flex items-end justify-around gap-4 h-40 pb-2">
        {segs.map((s) => {
          const hPct = s.value > 0 ? Math.max((s.value / maxVal) * 100, 6) : 0;
          return (
            <button
              key={s.key}
              type="button"
              onClick={() => go(s.statusFilter)}
              data-testid={`open-invoice-bar-${s.statusFilter.toLowerCase()}`}
              title={`${s.label}: ${fmtCAD(s.value)} — click to filter`}
              className="flex-1 flex flex-col items-center justify-end h-full group focus:outline-none"
            >
              {/* Vertical bar — height=0 when value=0, coloured baseline still visible below */}
              <div
                className="w-full rounded-t-sm transition-all group-hover:brightness-110"
                style={{ height: `${hPct}%`, background: s.color, minHeight: s.value > 0 ? 4 : 0 }}
              />
              {/* Coloured baseline — always visible, this is what shows the segment exists at $0 */}
              <div
                data-testid={`open-invoice-baseline-${s.statusFilter.toLowerCase()}`}
                className="w-full h-[3px]"
                style={{ background: s.color }}
              />
            </button>
          );
        })}
      </div>

      {/* Labels + amounts underneath each bar */}
      <div className="flex items-start justify-around gap-4 mt-2">
        {segs.map((s) => (
          <button
            key={s.key}
            type="button"
            onClick={() => go(s.statusFilter)}
            data-testid={`open-invoice-legend-${s.statusFilter.toLowerCase()}`}
            className="flex-1 text-center rounded hover:bg-slate-50 -mx-1 px-1 py-1 focus:outline-none focus:ring-1 focus:ring-[#0F4C81]"
          >
            <div className="font-semibold text-slate-900 text-sm tabular">{fmtCAD(s.value)}</div>
            <div className="text-[11px] text-slate-500 mt-0.5">{s.label}</div>
          </button>
        ))}
      </div>
    </Card>
  );
}

function PaidInvoicesCard({ data }) {
  return (
    <Card
      title="Paid invoices"
      icon={DollarSign}
      action={{ to: "/invoices?status=Paid", label: "Details" }}
      testid="card-paid-invoices"
    >
      <div className="flex items-end justify-between">
        <div>
          <div className="text-[11px] uppercase tracking-wider text-slate-500 font-medium">Paid</div>
          <div className="font-display text-3xl font-bold tabular text-slate-900 mt-1">{fmtCAD(data.total)}</div>
          <div className="text-xs text-slate-500 mt-1">all-time paid invoices</div>
        </div>
        <div className="text-right">
          <div className="text-[11px] uppercase tracking-wider text-slate-500 font-medium">Count</div>
          <div className="font-display text-2xl font-bold tabular text-slate-900 mt-1">{data.count}</div>
        </div>
      </div>
    </Card>
  );
}

function TodayStatsCard({ data, today_iso }) {
  const items = [
    { label: "Earned", value: fmtCAD(data.earned), testid: "today-earned" },
    { label: "Collected", value: fmtCAD(data.collected), testid: "today-collected" },
    { label: "Jobs completed", value: String(data.jobs_completed), testid: "today-jobs" },
    { label: "New jobs booked online", value: fmtCAD(data.new_booked_online), testid: "today-booked" },
  ];
  return (
    <Card
      title="Today's stats"
      icon={BarChart3}
      headerRight={<span className="text-xs text-slate-500">{new Date(today_iso).toLocaleDateString("en-CA", { day: "numeric", month: "short", year: "numeric" })}</span>}
      testid="card-today-stats"
    >
      <ul className="divide-y divide-slate-100">
        {items.map((it) => (
          <li key={it.label} data-testid={it.testid} className="flex items-center justify-between py-2.5 text-sm">
            <span className="text-slate-600">{it.label}</span>
            <span className="tabular font-semibold text-slate-900">{it.value}</span>
          </li>
        ))}
      </ul>
    </Card>
  );
}

function JobsCard({ data }) {
  return (
    <Card
      title="Jobs"
      icon={Briefcase}
      action={{ to: "/jobs", label: "All jobs" }}
      testid="card-jobs"
    >
      <div className="text-center pb-3 border-b border-slate-100">
        <div className="font-display text-3xl font-bold text-slate-900">{data.complete} <span className="text-base font-medium text-slate-500">complete</span></div>
        <div className="text-xs text-slate-500 mt-1 uppercase tracking-wider">All Time</div>
      </div>
      <div className="grid grid-cols-2 gap-3 mt-4">
        <Stat label={`${data.scheduled} sched`} value={fmtCAD(data.scheduled_value)} testid="jobs-scheduled" />
        <Stat label={`${data.unscheduled} unsched`} value="—" testid="jobs-unscheduled" />
      </div>
    </Card>
  );
}

function Stat({ label, value, testid }) {
  return (
    <div data-testid={testid} className="bg-slate-50 border border-slate-200 rounded-md px-3 py-2.5">
      <div className="font-display text-lg font-bold tabular text-slate-900">{value}</div>
      <div className="text-xs text-slate-500 mt-0.5">{label}</div>
    </div>
  );
}

function CorpTaxCard({ data }) {
  if (!data) return null;
  const { rate, revenue_ytd, expenses_ytd, net_profit_ytd, owed_ytd, balance } = data;
  const profitMargin = revenue_ytd > 0 ? (net_profit_ytd / revenue_ytd) * 100 : 0;
    return (
    <Card
      title="Corporation tax (YTD)"
      icon={Landmark}
      action={{ to: "/reports?tab=corp", label: "Run report" }}
      testid="card-corp-tax"
      headerRight={<span className="text-[10px] uppercase tracking-wider text-slate-500 font-medium bg-slate-100 px-1.5 py-0.5 rounded">ON · {rate}%</span>}
    >
      <div data-testid="corp-tax-owed" className="flex items-end justify-between border-b border-slate-100 pb-3">
        <div>
          <div className="text-[11px] uppercase tracking-wider text-slate-500 font-medium">Estimated tax owed</div>
          <div className="font-display text-3xl font-bold tabular text-slate-900 mt-1">{fmtCAD(balance || owed_ytd)}</div>
          <div className="text-xs text-slate-500 mt-1">on {fmtCAD(net_profit_ytd)} net profit · {profitMargin.toFixed(0)}% margin</div>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3 mt-3">
        <Stat label="Revenue YTD" value={fmtCAD(revenue_ytd)} testid="corp-tax-revenue" />
        <Stat label="Costs YTD" value={fmtCAD(expenses_ytd)} testid="corp-tax-costs" />
        <Stat label="Profit margin" value={`${profitMargin.toFixed(0)}%`} testid="corp-tax-margin" />
      </div>
    </Card>
  );
}

function HstCard({ data }) {
  if (!data) {
    return (
      <Card title="HST owed (YTD)" icon={Receipt} testid="card-hst" action={{ to: "/my-money", label: "View" }}>
        <div className="text-sm text-slate-400 italic py-6 text-center">No HST data yet</div>
      </Card>
    );
  }
  const { collected_ytd, owed_ytd, owed_mtd, trend } = data;
  return (
    <Card
      title="HST owed (YTD)"
      icon={Receipt}
      action={{ to: "/my-money", label: "Details" }}
      testid="card-hst"
      headerRight={<span className="text-[10px] uppercase tracking-wider text-emerald-700 font-medium bg-emerald-50 px-1.5 py-0.5 rounded">to CRA</span>}
    >
      <div data-testid="hst-owed" className="flex items-end justify-between border-b border-slate-100 pb-3">
        <div>
          <div className="text-[11px] uppercase tracking-wider text-slate-500 font-medium">Owed to CRA</div>
          <div className="font-display text-3xl font-bold tabular text-emerald-700 mt-1">{fmtCAD(owed_ytd)}</div>
          <div className="text-xs text-slate-500 mt-1">net of input tax credits</div>
        </div>
        {trend && trend.pct !== null && (
          <div className={`text-[11px] px-2 py-0.5 rounded font-medium ${trend.direction === "up" ? "bg-amber-100 text-amber-800" : "bg-emerald-100 text-emerald-800"}`}>
            {trend.direction === "up" ? "▲" : trend.direction === "down" ? "▼" : "—"} {Math.abs(trend.pct).toFixed(1)}% vs last mo
          </div>
        )}
      </div>
      <div className="grid grid-cols-2 gap-3 mt-3">
        <Stat label="Collected YTD" value={fmtCAD(collected_ytd)} testid="hst-collected" />
        <Stat label="Owed this month" value={fmtCAD(owed_mtd)} testid="hst-owed-mtd" />
      </div>
    </Card>
  );
}

/* ---------- Item 13 · TimesheetCard ---------- */
function TimesheetCard({ data }) {
  const hours = Number(data?.hours || 0);
  const wholeH = Math.floor(hours);
  const mins = Math.round((hours - wholeH) * 60);
  const range = data
    ? `${new Date(data.week_start).toLocaleDateString("en-CA", { month: "2-digit", day: "2-digit", year: "numeric" })} - ${new Date(data.week_end).toLocaleDateString("en-CA", { month: "2-digit", day: "2-digit", year: "numeric" })}`
    : "";
  return (
    <Card title="Timesheet" icon={Clock} action={{ to: "/timesheets", label: "View" }} testid="card-timesheet">
      <div className="text-center py-6">
        <div className="text-xs text-slate-500">{range}</div>
        <div data-testid="timesheet-hours" className="font-display text-4xl font-bold tabular text-slate-900 mt-4">
          {wholeH}:{String(mins).padStart(2, "0")} <span className="text-lg font-medium text-slate-500">hours</span>
        </div>
        <div className="text-[11px] text-slate-500 mt-1">this week</div>
      </div>
    </Card>
  );
}

/* ---------- Item 13 · EstimatesDonutCard ---------- */
const EST_COLORS = { won: "#10B981", open: "#2563EB", lost: "#64748B" };

function EstimatesDonutCard({ data }) {
  const won = data?.won || { count: 0, total: 0 };
  const open = data?.open || { count: 0, total: 0 };
  const lost = data?.lost || { count: 0, total: 0 };
  const created = data?.created || { count: 0, total: 0 };

  const totalCount = won.count + open.count + lost.count;
  const wonPct = totalCount ? Math.round((won.count / totalCount) * 100) : 0;

  // SVG donut segments — sized by count
  const size = 140;
  const stroke = 22;
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const parts = [
    { key: "won",  count: won.count,  color: EST_COLORS.won },
    { key: "open", count: open.count, color: EST_COLORS.open },
    { key: "lost", count: lost.count, color: EST_COLORS.lost },
  ];
  let offset = 0;
  const segs = parts.map((p) => {
    const share = totalCount ? p.count / totalCount : 0;
    const len = share * c;
    const seg = { ...p, len, offset };
    offset += len;
    return seg;
  });

  return (
    <Card title="Estimates" icon={FileText} action={{ to: "/estimates", label: "All estimates" }} testid="card-estimates-donut">
      {/* Stat row — Created / Open / Lost / Won with count + dollar total */}
      <div className="grid grid-cols-4 gap-2 text-center mb-3 border-b border-slate-100 pb-3">
        <div data-testid="est-donut-created">
          <div className="font-semibold text-slate-900 text-sm tabular">{fmtCAD(created.total)}</div>
          <div className="text-[10px] text-slate-500 mt-0.5">{created.count} created</div>
        </div>
        <div data-testid="est-donut-open">
          <div className="font-semibold text-sm tabular" style={{ color: EST_COLORS.open }}>{fmtCAD(open.total)}</div>
          <div className="text-[10px] mt-0.5" style={{ color: EST_COLORS.open }}>{open.count} open</div>
        </div>
        <div data-testid="est-donut-lost">
          <div className="font-semibold text-sm tabular text-slate-600">{fmtCAD(lost.total)}</div>
          <div className="text-[10px] text-slate-600 mt-0.5">{lost.count} lost</div>
        </div>
        <div data-testid="est-donut-won">
          <div className="font-semibold text-sm tabular" style={{ color: EST_COLORS.won }}>{fmtCAD(won.total)}</div>
          <div className="text-[10px] mt-0.5" style={{ color: EST_COLORS.won }}>{won.count} won</div>
        </div>
      </div>

      <div className="text-center text-xs text-slate-500 mb-1">All Time</div>

      {/* SVG donut — real segments proportional to real counts. When no estimates, shows the empty grey ring. */}
      <div className="flex items-center justify-center py-2">
        <div className="relative" style={{ width: size, height: size }}>
          <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
            {/* Base ring */}
            <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="#E2E8F0" strokeWidth={stroke} />
            {/* Data segments */}
            {totalCount > 0 && segs.map((s) => s.count > 0 && (
              <circle
                key={s.key}
                cx={size / 2}
                cy={size / 2}
                r={r}
                fill="none"
                stroke={s.color}
                strokeWidth={stroke}
                strokeDasharray={`${s.len} ${c - s.len}`}
                strokeDashoffset={-s.offset}
                transform={`rotate(-90 ${size / 2} ${size / 2})`}
              />
            ))}
          </svg>
          <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
            <div data-testid="est-donut-pct" className="font-display text-2xl font-bold text-slate-900 tabular">{wonPct}%</div>
            <div className="text-[11px] text-slate-500">won</div>
          </div>
        </div>
      </div>

      {totalCount === 0 && (
        <div className="text-center text-xs text-slate-400 italic mt-1">No estimates yet</div>
      )}
    </Card>
  );
}
